"use client";

import { useId, useState, type FormEvent } from "react";
import { WohaRichText } from "../WohaRichText/WohaRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-12 w-full rounded-lg border border-charcoal bg-white px-4 text-base text-charcoal outline-none transition-[border-color,box-shadow] placeholder:text-fog focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/15 aria-[invalid=true]:border-red-700 aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-red-700/10";

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};

    for (const control of controls) {
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }

    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("Submission is unavailable while offline.");
      await new Promise<void>((resolve) => window.setTimeout(resolve, 350));
      setSucceeded(true);
    } catch {
      setSubmissionFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="rounded-2xl border border-ash bg-paper-mist p-7 md:p-10"
        role="status"
      >
        <div className="mb-5 flex size-11 items-center justify-center rounded-full bg-soft-mint text-xl text-vivid-green">
          ✓
        </div>
        <WohaRichText
          className="max-w-[65ch] text-lg leading-8 text-graphite [&_h3]:font-satoshi [&_h3]:text-2xl [&_h3]:font-medium [&_h3]:leading-tight [&_p]:mt-2"
          content={successMessage}
        />
      </div>
    );

  return (
    <form className="flex flex-col gap-6" noValidate onSubmit={submit}>
      {submissionFailed ? (
        <div className="rounded-xl border border-red-700/30 bg-red-50 p-5" role="alert">
          <WohaRichText className="text-base leading-7 text-red-900" content={errorMessage} />
        </div>
      ) : null}

      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const describedBy = error ? errorId : undefined;
        const label = (
          <span className="text-sm font-medium text-charcoal">
            {field.label}
            {field.required ? <span className="ml-1 text-steel">(required)</span> : null}
          </span>
        );
        const shared = {
          "aria-describedby": describedBy,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          required: Boolean(field.required),
        };

        return (
          <div className="flex flex-col gap-2" key={`${field.label}-${index}`}>
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="size-5 shrink-0 rounded border-charcoal accent-charcoal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                <label htmlFor={inputId}>{label}</label>
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select {...shared} className={controlClass} defaultValue="">
                    <option disabled value="">
                      {field.placeholder || "Select an option"}
                    </option>
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    className={controlClass}
                    pattern={field.fieldType === "phone" ? "[+()0-9 .-]{7,}" : undefined}
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p className="text-sm text-red-700" id={errorId}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}

      <button
        className="inline-flex min-h-12 w-full items-center justify-center rounded-lg border border-charcoal bg-charcoal px-6 text-sm font-medium text-white transition-colors hover:bg-graphite focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44"
        disabled={submitting}
        type="submit"
      >
        {submitting ? "Submitting…" : submitLabel}
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
